"use client";

import { createContext, useCallback, useContext, useState } from "react";
import { CheckCircle2, AlertCircle, X } from "lucide-react";

const ToastContext = createContext(null);
let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, { type = "success", duration = 3000 } = {}) => {
    const id = ++idCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      <div className="fixed top-5 right-5 z-[200] flex flex-col gap-2.5 items-end pointer-events-none max-w-[calc(100vw-2.5rem)]">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto flex items-center gap-2.5 pl-4 pr-3 py-3 rounded-xl shadow-lg border text-sm font-medium max-w-sm animate-in fade-in slide-in-from-top-2 duration-200 ${
              t.type === "error"
                ? "bg-red-50 border-red-200 text-red-700"
                : "bg-green-50 border-green-200 text-green-800"
            }`}
          >
            {t.type === "error" ? (
              <AlertCircle size={16} className="text-red-500 shrink-0" />
            ) : (
              <CheckCircle2 size={16} className="text-green-600 shrink-0" />
            )}
            <span className="flex-1">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="text-mute hover:text-ink transition-colors cursor-pointer shrink-0"
            >
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TOAST_FALLBACK = { showToast: () => {}, dismiss: () => {} };

export function useToast() {
  const ctx = useContext(ToastContext);
  return ctx ?? TOAST_FALLBACK;
}
